import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './UserRated.css';
import { useNavigate } from './Router';
import { FaStar } from 'react-icons/fa';

const UserRated = ({ 
  user_id, 
  variant = 'default', // 'default', 'subtle', 'dark'
  size = 'default', // 'small', 'default', 'large'
  hideIfNoRating = true, // Masquer si pas de rating
  showCount = false, // Afficher le nombre d'évaluations
  className = '' 
}) => {
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user_id) {
      setLoading(false);
      setError(true);
      return;
    }

    const fetchRating = async () => {
      try {
        setLoading(true);
        setError(false);
        
        const ratingData = await sdk.models.TaskReview.getRated({ user_id });
        
        if (ratingData && ratingData.count > 0) {
          setRating(ratingData);
        } else {
          setRating({ count: 0, rated: 0 });
        }
      } catch (err) {
        console.error('Erreur lors de la récupération du rating:', err);
        setError(true);
        setRating({ count: 0, rated: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchRating();
  }, [user_id]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (user_id) {
      navigate(`/travailleur/${user_id}`);
    }
  };

  // Ne rien afficher si en cours de chargement et hideIfNoRating est true
  if (loading) {
    if (hideIfNoRating) return null;
    
    return (
      <span className={`ur-loading ${className}`}>
        <div className="ur-loading-spinner"></div>
      </span>
    );
  }

  // Ne rien afficher en cas d'erreur ou si pas de rating et hideIfNoRating est true
  if (error || !rating || (hideIfNoRating && rating.count === 0)) {
    return null;
  }

  // Ne rien afficher si pas d'évaluations et hideIfNoRating est true
  if (rating.count === 0 && hideIfNoRating) {
    return null;
  }

  // Construire les classes CSS
  const containerClasses = [
    'ur-container',
    variant !== 'default' ? `ur-${variant}` : '',
    size !== 'default' ? `ur-${size}` : '',
    className
  ].filter(Boolean).join(' ');

  // Formater le rating pour l'affichage
  const formatRating = (rating) => {
    if (rating === 0) return '0';
    return rating % 1 === 0 ? rating.toString() : rating.toFixed(1);
  };

  // Construire le texte d'info-bulle
  const getTooltip = () => {
    if (rating.count === 0) {
      return "Aucune évaluation";
    }
    
    const ratingText = `${formatRating(rating.rated)}/5 étoiles`;
    const countText = `${rating.count} évaluation${rating.count > 1 ? 's' : ''}`;
    
    return `${ratingText} - ${countText}`;
  };

  return (
    <span
      className={containerClasses}
      onClick={handleClick}
      title={getTooltip()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      }}
    >
      <FaStar className="ur-star" />
      <span className="ur-rating">
        {formatRating(rating.rated)}
        {showCount && rating.count > 0 && (
          <span style={{ fontSize: '0.9em', opacity: 0.7 }}>
            ({rating.count})
          </span>
        )}
      </span>
    </span>
  );
};

export default UserRated;
export { UserRated };